import { redactMcpConsentRoute } from "./mcpPrivacy";
/**
 * Frontend error logger.
 * Posts errors to /api/v1/bug-reports for server-side tracking.
 * Never uses console.log — all output goes to the API.
 */

interface BugReport {
  readonly message: string;
  readonly stack?: string;
  readonly route: string;
  readonly userAgent: string;
  readonly timestamp: string;
  readonly componentStack?: string;
  readonly extra?: Record<string, unknown>;
}

import { COOKIE_CSRF } from "./cookieNames";

function getCsrfToken(): string | undefined {
  const prefix = `${COOKIE_CSRF}=`;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  return match ? match.substring(prefix.length) : undefined;
}

/**
 * Posts an error report to the backend bug-report endpoint.
 * Fire-and-forget — never throws. Errors during reporting are silently dropped.
 */
export async function reportError(
  error: Error,
  options: {
    componentStack?: string;
    extra?: Record<string, unknown>;
  } = {},
): Promise<void> {
  const report: BugReport = {
    message: error.message,
    stack: error.stack,
    route: redactMcpConsentRoute(window.location.pathname + window.location.search),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    componentStack: options.componentStack,
    extra: options.extra,
  };

  try {
    const csrfToken = getCsrfToken();
    await fetch("/api/v1/bug-reports", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify(report),
    });
  } catch {
    // Silently drop — we can't report the reporter failing.
  }
}
